import { memo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';
import { formatTimeAgo } from '../../utils/dateUtils';
import { getNotificationChatId, getNotificationRoute } from '../../utils/notificationNavigation';
import { getNotificationIcon } from './notificationIcon';
import { useChatDestination } from '../../hooks/chats/useChatDestination';
import { type Notification, JobStatus } from '../../generated/graphql';

// Memoized NotificationItem component to prevent unnecessary re-renders
interface NotificationItemProps {
  notification: Notification;
  onClose?: () => void;
  onMarkAsRead?: (id: string) => void | Promise<unknown>;
}

const NotificationItem = memo<NotificationItemProps>(({ notification, onClose, onMarkAsRead }) => {
  const navigate = useNavigate();
  const resolveChatDestination = useChatDestination();
  const apolloClient = useApolloClient();
  const route = getNotificationRoute(notification);
  const notificationIcon = getNotificationIcon(notification);
  const chatId = getNotificationChatId(notification);
  const isClickable = route !== null;
  const isOpening = useRef(false);

  let metadata: Record<string, unknown> | null = null;
  if (notification.metadata) {
    if (typeof notification.metadata === 'string') {
      try {
        metadata = JSON.parse(notification.metadata) as Record<string, unknown>;
      } catch {
        metadata = {};
      }
    } else if (typeof notification.metadata === 'object') {
      metadata = notification.metadata as Record<string, unknown>;
    }
  }

  let dataObj: Record<string, unknown> | null = null;
  if (notification.data) {
    if (typeof notification.data === 'string') {
      try {
        dataObj = JSON.parse(notification.data) as Record<string, unknown>;
      } catch {
        dataObj = {};
      }
    } else if (typeof notification.data === 'object') {
      dataObj = notification.data as Record<string, unknown>;
    }
  }

  let jobId = (metadata?.jobId || metadata?.job_id || dataObj?.jobId || dataObj?.job_id) as
    | string
    | undefined;
  if (!jobId && route && route.startsWith('/jobs/')) {
    const match = route.match(/\/jobs\/([a-zA-Z0-9_-]+)/);
    if (match) jobId = match[1];
  }

  const handleClick = () => {
    if (!route || isOpening.current) return;
    isOpening.current = true;

    if (!notification.readAt && onMarkAsRead) {
      try {
        void Promise.resolve(onMarkAsRead(notification.id))
          .catch(error => console.error('Failed to mark notification as read:', error))
          .finally(() => {
            isOpening.current = false;
          });
      } catch (error) {
        console.error('Failed to mark notification as read:', error);
        isOpening.current = false;
      }
    } else {
      isOpening.current = false;
    }

    if (jobId) {
      const typeStr = (notification.type || '').toUpperCase();
      const titleStr = (notification.title || '').toLowerCase();
      let targetStatus: JobStatus | null = null;

      if (typeStr === 'JOB_COMPLETED' || titleStr.includes('completed')) {
        targetStatus = JobStatus.Completed;
      } else if (typeStr === 'JOB_CONFIRMED' || titleStr.includes('confirmed')) {
        targetStatus = JobStatus.Confirmed;
      } else if (
        typeStr === 'JOB_CANCELLED' ||
        typeStr === 'JOB_CANCELED' ||
        titleStr.includes('canceled') ||
        titleStr.includes('cancelled')
      ) {
        targetStatus = JobStatus.Canceled;
      } else if (typeStr === 'JOB_STARTED' || titleStr.includes('started')) {
        targetStatus = JobStatus.Started;
      }

      if (targetStatus) {
        const entityId = apolloClient.cache.identify({ __typename: 'Job', id: jobId });
        if (entityId) {
          apolloClient.cache.modify({
            id: entityId,
            fields: {
              status() {
                return targetStatus;
              },
            },
          });
        }
      }
    }

    void apolloClient.refetchQueries({
      include: [
        'GetNotifications',
        'GetUnreadCount',
        'GetJobs',
        'GetJobsListPage',
        'getJob',
        'GetJob',
      ],
    });

    // A message about a job, an estimate, an invoice or an appointment opens
    // that record, which is where its conversation is read; only a direct
    // chat has nothing to open but the inbox.
    if (chatId) {
      void resolveChatDestination(chatId).then(
        destination => navigate(destination),
        () => navigate(route)
      );
    } else {
      navigate(route);
    }

    if (onClose) {
      onClose();
    }
  };

  return (
    <div
      className={`px-5 py-3 transition-colors border-b border-ds-border-light last:border-b-0 ${
        !notification.readAt ? 'bg-ds-bg-subtle' : 'bg-white'
      } ${isClickable ? 'cursor-pointer hover:bg-ds-bg-secondary active:bg-ds-bg' : ''}`}
      data-notification-id={notification.id}
      onClick={handleClick}
    >
      <div className="flex items-start gap-3.5">
        {/* What the notification is about, at a glance. The rows carried no
            icon at all, so an expired estimate and a new device login read
            the same from across the panel. */}
        <div
          className={`w-8 h-8 rounded-[8px] flex items-center justify-center flex-shrink-0 ${notificationIcon.toneClass}`}
        >
          {notificationIcon.icon}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-[13px] leading-5 text-ds-text-primary truncate">
                {notification.title}
              </h4>
              {/* Two lines and an ellipsis. The body used to run its full
                  length, so one wordy notification pushed the rest of the
                  list out of the panel. */}
              <p
                className="text-xs leading-5 text-ds-text-secondary line-clamp-2"
                title={notification.body || ''}
              >
                {notification.body || ''}
              </p>
            </div>

            {/* The timestamp shares the row with the title and never shrinks,
                so at 280px it took most of the width and left the copy about
                70px to wrap in. Small and nowrap, it stays on one line. */}
            <div className="flex items-center gap-2 flex-shrink-0 pt-0.5">
              {!notification.readAt && (
                <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0"></div>
              )}
              <span className="text-[11px] leading-none text-ds-text-tertiary whitespace-nowrap">
                {formatTimeAgo(notification.createdAt)}
              </span>
            </div>
          </div>

          {/* The call to action the backend sends with the notification —
              "VIEW ESTIMATE", "VIEW SETTINGS". It was being dropped, so the
              row said what had happened and offered no way to act on it. */}
          {notification.actionText && (
            <button
              type="button"
              onClick={event => {
                event.stopPropagation();
                handleClick();
              }}
              className="mt-1 text-xs font-bold text-primary uppercase tracking-wide hover:underline"
            >
              {notification.actionText}
            </button>
          )}
        </div>
      </div>
    </div>
  );
});

NotificationItem.displayName = 'NotificationItem';

export default NotificationItem;
